import config from '../config';

const PROMISE_KEY = '__googleMapsLoadPromise';
const CALLBACK_NAME = '__onGoogleMapsLoaded';
const SCRIPT_ORIGIN = 'maps.googleapis.com/maps/api/js';

// Helper: Poll for window.google.maps
// resolve: function to call when maps is ready
// reject: function to call on timeout
// attempts/maxAttempts: counters for timeout
// interval: time between checks in ms
const poll = (resolve, reject, attempts = 0, maxAttempts = 150, interval = 100) => {
    if (window.google?.maps) return resolve(window.google);
    
    if (attempts >= maxAttempts) {
        // Clean up so a future retry is possible
        delete window[PROMISE_KEY];
        return reject(new Error(
            `Google Maps did not initialize after ${(maxAttempts * interval) / 1000}s. ` +
            `This is often caused by an ad-blocker, CSP policy, or invalid API key. ` +
            `Check DevTools Network tab for blocked requests to maps.googleapis.com.`
        ));
    }
    
    setTimeout(() => poll(resolve, reject, attempts + 1, maxAttempts, interval), interval);
};

export const loadGoogleMaps = () => {
    // 1. Already fully loaded — resolve immediately.
    if (window.google?.maps) return Promise.resolve(window.google);
    
    // 2. A load is already in progress (same or different module instance).
    //    Return the existing promise so all callers share one resolution.
    if (window[PROMISE_KEY]) return window[PROMISE_KEY];

    const apiKey = config.GOOGLE_MAPS_API_KEY;
    if (!apiKey) return Promise.reject(new Error('Google Maps API key is missing in config'));

    window[PROMISE_KEY] = new Promise((resolve, reject) => {
        // 3. Define the global callback BEFORE injecting the script.
        //    Google Maps will call this once the API is fully bootstrapped.
        window[CALLBACK_NAME] = () => {
            delete window[CALLBACK_NAME];
            // Don't resolve immediately — let poll() confirm the object is real.
            // This guards against Google calling the callback before maps is fully ready.
            poll(resolve, reject, 0, 20, 50); // fast poll, max 1s after callback
        };

        // 4. Check if a script tag already exists in the DOM.
        const existingScript = document.querySelector(`script[src*="${SCRIPT_ORIGIN}"]`);

        if (existingScript) {
            // Script exists but wasn't ours — callback won't fire.
            // Check if the existing script already has our callback in its src.
            const hasCallback = existingScript.src.includes(CALLBACK_NAME);
            if (!hasCallback) {
                // Foreign script: poll-only strategy
                delete window[CALLBACK_NAME];
                return poll(resolve, reject);
            }
            // Our script is already loading — just poll and let callback also resolve
            return poll(resolve, reject);
        }

        // 5. Inject the script tag.
        const script = document.createElement('script');
        script.type = 'text/javascript';
        // Use legacy callback mechanism (no loading=async) for maximum reliability
        // Request the "marker" library for AdvancedMarkerElement
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,marker&callback=${CALLBACK_NAME}`;
        script.async = true;
        script.defer = true;

        script.onerror = () => {
            delete window[CALLBACK_NAME];
            delete window[PROMISE_KEY]; // allow retry
            reject(new Error(
                'Google Maps script request failed (network error or invalid API key). ' +
                'Check the browser Network tab for the exact HTTP status.'
            ));
        };

        // Safety net: if callback never fires within 15s, fall back to polling resolution.
        // This catches the "ad-blocker stalls bootstrap" scenario.
        const callbackTimeout = setTimeout(() => {
            if (window[CALLBACK_NAME]) {
                // Callback hasn't fired yet — switch to pure polling
                console.warn(
                    'Google Maps callback did not fire within 15s. ' +
                    'Falling back to polling. Check for ad-blockers blocking gen_204 requests.'
                );
                // We don't delete the callback, just in case it fires late, 
                // but we start polling now to unblock the UI.
                poll(resolve, reject); 
            }
        }, 15000);

        // Wrap the callback to ensure timeout is cleared if it fires normally
        const originalCallback = window[CALLBACK_NAME];
        window[CALLBACK_NAME] = () => {
            clearTimeout(callbackTimeout);
            if (originalCallback) originalCallback();
        };

        document.head.appendChild(script);
    });

    return window[PROMISE_KEY];
};

export const isGoogleMapsLoaded = () => !!window.google?.maps;
